import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { CalculationBenefitDetailsService } from '../../services';
import {
  GetBankConfigurationResponse,
  GetCalculationBenefitDetailsResponse,
  GetListEmployerTypesResponse,
} from '../../services/models';
import {
  closeBenefitWarningMessageAction,
  closeBenefitWarningMessageFailureAction,
  closeBenefitWarningMessageSuccessAction,
  createGenerateCalculationWorksheetAction,
  createGenerateCalculationWorksheetFailureAction,
  createGenerateCalculationWorksheetSuccessAction,
  getBankConfigurationAction,
  getBankConfigurationFailureAction,
  getBankConfigurationSuccessAction,
  getCalculationBenefitDetailsAction,
  getCalculationBenefitDetailsFailureAction,
  getCalculationBenefitDetailsSuccessAction,
  getListEmployerTypesAction,
  getListEmployerTypesFailureAction,
  getListEmployerTypesSuccessAction,
  setBenefitDetailAction,
  setBenefitDetailFailureAction,
  setBenefitDetailSuccessAction,
} from '../actions';

@Injectable()
export class GetCalculationBenefitDetailsEffects {
  constructor(
    private actions$: Actions,
    private calculationBenefitDetailsService: CalculationBenefitDetailsService,
  ) {}

  getCalculationBenefitDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCalculationBenefitDetailsAction),
      switchMap(({ request }) =>
        this.calculationBenefitDetailsService.getCalculationBenefitDetails(request).pipe(
          map((response: GetCalculationBenefitDetailsResponse) =>
            getCalculationBenefitDetailsSuccessAction({ response }),
          ),
          catchError((error) => of(getCalculationBenefitDetailsFailureAction({ error }))),
        ),
      ),
    ),
  );

  setBenefitDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setBenefitDetailAction),
      switchMap(({ params, request, editItem }) => {
        return this.calculationBenefitDetailsService.setBenefitDetail(params, request).pipe(
          map(() => setBenefitDetailSuccessAction({ editItem })),
          catchError((error) => of(setBenefitDetailFailureAction({ error, editItem }))),
        );
      }),
    ),
  );

  closeBenefitDetailWarningMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(closeBenefitWarningMessageAction),
      switchMap(({ params }) => {
        return this.calculationBenefitDetailsService.calculationParameterCloseWarningMessage(params).pipe(
          map(() => closeBenefitWarningMessageSuccessAction()),
          catchError((error) => of(closeBenefitWarningMessageFailureAction({ error }))),
        );
      }),
    ),
  );

  createGenerateCalculationWorksheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createGenerateCalculationWorksheetAction),
      switchMap(({ memberId, request }) =>
        this.calculationBenefitDetailsService.createGenerateCalculationWorksheet(memberId, request).pipe(
          map((res: any) => {
            const contentDisposition = res.headers.get('content-disposition');
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(contentDisposition);
            let fileName = '';
            if (matches?.[1]) {
              fileName = matches[1].replace(/['"\s]/g, '');
            }
            const response = {
              fileName,
              file: [res.body],
            };
            return createGenerateCalculationWorksheetSuccessAction({ response });
          }),
          catchError((error) => of(createGenerateCalculationWorksheetFailureAction({ error }))),
        ),
      ),
    ),
  );

  getListEmployerTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getListEmployerTypesAction),
      switchMap(({ request }) =>
        this.calculationBenefitDetailsService.getListEmployerTypes(request).pipe(
          map((response: GetListEmployerTypesResponse) => getListEmployerTypesSuccessAction({ response })),
          catchError((error) => of(getListEmployerTypesFailureAction({ error }))),
        ),
      ),
    ),
  );

  getBankConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBankConfigurationAction),
      switchMap(() =>
        this.calculationBenefitDetailsService.getBankConfiguration().pipe(
          map((response: GetBankConfigurationResponse) => getBankConfigurationSuccessAction({ response })),
          catchError((error) => of(getBankConfigurationFailureAction({ error })))
        )
      )
    )
  );

}
